import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { JobAction, JobActionTimePeriod } from '../../action.types';
import { Holiday, BankHoliday, NonWorkingDays } from '@intranet/modules/holidays/holiday.types';
import { PlannedBlockViewModel } from './planned-block.types';

// In-memory cache for everything the Planner loads, kept at root level so
// it survives the Planner component itself being destroyed — switching to
// List/Board and back removes <planner> outright (*ngIf in
// action-listing.component.html), which used to mean re-downloading the
// lot every time. PlannerComponent shows whatever's cached here straight
// away and refreshes it quietly in the background (same idea as the
// Actions page's own "kept results" on a view switch), so nothing visibly
// reloads but nothing goes stale either.
//
// Memory only (no localStorage) — a page reload starts clean, and so does
// a logout (both Azure and plain logout leave the page). Every entry is
// also scoped to the logged-in user via setViewer, as a belt-and-braces
// guard against one user's cached planner ever showing for another.
@Injectable({
    providedIn: 'root',
})
export class PlannerCacheService {
    private _viewerId: string | undefined;
    private _blocks = new Map<string, PlannedBlockViewModel[]>();
    // Bumped per subject user on every invalidateBlocks — lets a fetch that
    // started before a create/move/delete tell that its (now outdated)
    // reply mustn't be written into the cache. See blocksVersion.
    private _blockVersions = new Map<string, number>();
    private _holidays = new Map<string, { holidays: Holiday[]; bankHolidays: BankHoliday[] }>();
    private _nonWorkingDays = new Map<string, NonWorkingDays[]>();
    private _unplanned = new Map<string, JobAction[]>();

    /** Drops everything if the logged-in user has changed since last use. */
    setViewer(viewerId: string | undefined): void {
        if (viewerId === this._viewerId) return;
        this._viewerId = viewerId;
        this._blocks.clear();
        this._blockVersions.clear();
        this._holidays.clear();
        this._nonWorkingDays.clear();
        this._unplanned.clear();
    }

    // ─── Planned blocks, per subject user + exact fetched date range ────

    blocksKey(userId: string, start: Date, end: Date): string {
        return `${userId}|${start.toISOString()}|${end.toISOString()}`;
    }

    getBlocks(key: string): PlannedBlockViewModel[] | undefined {
        return this._blocks.get(key);
    }

    setBlocks(key: string, blocks: PlannedBlockViewModel[]): void {
        this._blocks.set(key, blocks);
    }

    blocksVersion(userId: string): number {
        return this._blockVersions.get(userId) ?? 0;
    }

    /** An action's own details were edited (the Actions page's edit popup,
     *  opened from the Planner): its blocks in every cached range take the
     *  new name, job, client and deadline, so the Planner can redraw them
     *  from here at once, without asking the server again — the blocks
     *  themselves (when, how long, whose) haven't changed. */
    updateActionDetails(action: JobAction): void {
        const deadlineDate = action.deadlineDate ? new Date(action.deadlineDate).toISOString() : null;
        this._blocks.forEach((blocks) => blocks.forEach((b) => {
            if (b.isBusy || b.jobActionId !== action.jobActionId) return;
            b.jobActionTitle = action.title ?? b.jobActionTitle;
            b.jobId = action.jobId ?? b.jobId;
            b.jobNumber = action.jobNumber ?? b.jobNumber;
            b.jobTitle = action.jobTitle ?? b.jobTitle;
            b.clientName = action.clientName ?? b.clientName;
            b.isBillable = action.isBillable ?? b.isBillable;
            b.deadlineDate = deadlineDate;
        }));
    }

    /** The pull-out's own When filter — remembered for the session (across
     *  view switches) but not across reloads, so each visit to the site
     *  starts from the spec's default, Today. */
    pulloutWhen: JobActionTimePeriod = JobActionTimePeriod.Today;
    /** The pull-out's open tab — same session-only memory as pulloutWhen. */
    pulloutTab: 'unplanned' | 'planned' = 'unplanned';
    /** People whose sections are collapsed (several-people view) — same
     *  session-only memory. */
    collapsedPeople = new Set<string>();

    /** Fires on every change to anyone's blocks (see invalidateBlocks) — the
     *  pull-out re-checks which actions are Planned on it. */
    readonly blocksInvalidated$ = new Subject<void>();

    /** Fires when blocks were changed from outside the Planner (e.g. future
     *  blocks removed after completing an action) — an open Planner
     *  re-renders on it. */
    readonly blocksChangedElsewhere$ = new Subject<void>();

    /** Every user's cached blocks dropped — for changes that can touch
     *  several people's planners at once (an action's blocks can sit on
     *  anyone's), then tells any open Planner (blocksChangedElsewhere$),
     *  which re-renders. The Planner's own changes pass notifyPlanner:
     *  false — it has already updated its calendars itself, and telling it
     *  made every calendar reload (the loading spinner flashing). */
    invalidateAllBlocks(notifyPlanner = true): void {
        const userIds = new Set<string>([
            ...Array.from(this._blockVersions.keys()),
            ...Array.from(this._blocks.keys()).map((k) => k.split('|')[0]),
        ]);
        userIds.forEach((userId) => this.invalidateBlocks(userId));
        this._blocks.clear();
        if (notifyPlanner) this.blocksChangedElsewhere$.next();
    }

    /** Called after any change to a user's blocks (create/move/resize/
     *  delete/undo) — every cached range for them is dropped, since a move
     *  can take a block from one week into another. */
    invalidateBlocks(userId: string): void {
        const prefix = `${userId}|`;
        Array.from(this._blocks.keys())
            .filter((k) => k.startsWith(prefix))
            .forEach((k) => this._blocks.delete(k));
        this._blockVersions.set(userId, this.blocksVersion(userId) + 1);
        this.blocksInvalidated$.next();
    }

    // ─── Absence data, per subject user ─────────────────────────────────

    getHolidays(userId: string): { holidays: Holiday[]; bankHolidays: BankHoliday[] } | undefined {
        return this._holidays.get(userId);
    }

    setHolidays(userId: string, value: { holidays: Holiday[]; bankHolidays: BankHoliday[] }): void {
        this._holidays.set(userId, value);
    }

    getNonWorkingDays(userId: string): NonWorkingDays[] | undefined {
        return this._nonWorkingDays.get(userId);
    }

    setNonWorkingDays(userId: string, value: NonWorkingDays[]): void {
        this._nonWorkingDays.set(userId, value);
    }

    // ─── Unscheduled pull-out list, per exact search query ──────────────

    getUnplanned(key: string): JobAction[] | undefined {
        return this._unplanned.get(key);
    }

    setUnplanned(key: string, actions: JobAction[]): void {
        this._unplanned.set(key, actions);
    }
}
